import React, { useEffect, useState } from "react";
import axios from "axios";
import { Megaphone, X } from "lucide-react";

// Founder-authored "we changed something" banner, shown on Dispatch home
// and Operational Center (see pages/dispatcher/Announcements.jsx for where
// these get published). Deliberately NOT a modal and NOT permanently
// dismissible -- dismissing here only clears it from THIS already-loaded
// page (plain component state, nothing persisted), so it never blocks or
// hides anything a dispatcher actually needs, but it also can't be
// "read once and forgotten" -- it reappears on their next visit until the
// admin-set duration_days actually runs out (or the admin retracts it
// early from the Announcements page). Uses CSS vars (--card/--line/--ink/
// --muted/--brand) that both the light dispatcher shell and DispatcherBoard's
// own dark theme already define, so one component renders correctly in
// both contexts without a theme prop.
export default function AnnouncementBanner({ apiBaseUrl, authHeaders }) {
  const [rows, setRows] = useState([]);
  const [dismissedIds, setDismissedIds] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${apiBaseUrl}/dispatch_announcements/?limit=50`, authHeaders())
      .then((res) => {
        if (cancelled) return;
        const now = Date.now();
        const active = (res.data.records || res.data || []).filter(
          (r) => r.active && new Date(r.expires_at).getTime() > now
        );
        setRows(active);
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visible = rows.filter((r) => !dismissedIds.has(r.id));
  if (visible.length === 0) return null;

  return (
    <div style={styles.stack}>
      {visible.map((row) => (
        <div key={row.id} style={styles.banner}>
          <Megaphone size={15} style={styles.icon} />
          <div style={styles.message}>{row.message}</div>
          <button
            style={styles.closeBtn}
            onClick={() => setDismissedIds((prev) => new Set(prev).add(row.id))}
            title="Dismiss for now"
          >
            <X size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}

const styles = {
  stack: { display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 },
  banner: {
    display: "flex", alignItems: "flex-start", gap: 10,
    background: "rgba(37,99,235,0.08)", border: "1px solid rgba(37,99,235,0.25)",
    borderRadius: "var(--radius-md, 8px)", padding: "10px 12px",
  },
  icon: { color: "var(--brand)", flexShrink: 0, marginTop: 1 },
  message: { flex: 1, fontSize: 12.5, color: "var(--ink)", lineHeight: 1.5, whiteSpace: "pre-wrap" },
  closeBtn: {
    display: "flex", alignItems: "center", justifyContent: "center",
    background: "transparent", border: "none", color: "var(--muted)", cursor: "pointer",
    padding: 2, flexShrink: 0,
  },
};
